/**
 * Video Lazy Loading Module
 * Loads videos only when they are within 300px of the viewport
 * Optimizes page load performance by deferring video loading
 */
(function() {
  'use strict';

  // Configuration
  const CONFIG = {
    threshold: 300, // Distance in pixels from viewport to trigger loading
    rootMargin: '300px' // Extend the root margin for Intersection Observer
  };

  // State management
  const state = {
    observer: null,
    loadedVideos: new Set(),
    initialized: false
  };

  /**
   * Get video source URL from data attribute
   * Handles both direct URLs and Shopify video objects
   */
  function getVideoSource(videoElement) {
    const lazySrc = videoElement.getAttribute('data-lazy-video-src');
    if (!lazySrc) return null;
    console.log('lazySrc', lazySrc);
    // If it's a JSON string (Shopify video object), parse it
    try {
      const videoData = JSON.parse(lazySrc);
      
      // Shopify video objects have a 'sources' array
      if (videoData.sources && Array.isArray(videoData.sources) && videoData.sources.length > 0) {
        // Filter all MP4 sources
        const mp4Sources = videoData.sources.filter(s => 
          (s.format && s.format.toLowerCase() === 'mp4') || 
          (s.mime_type && s.mime_type.toLowerCase().includes('mp4')) ||
          (s.url && s.url.toLowerCase().includes('.mp4'))
        );
        
        if (mp4Sources.length > 0) {
          // Sort by width (highest first) to get maximum quality
          // If width is not available, use the last source (Shopify orders lowest to highest)
          const sortedSources = mp4Sources.sort((a, b) => {
            const widthA = a.width || 0;
            const widthB = b.width || 0;
            return widthB - widthA;
          });
          
          if (sortedSources[0] && sortedSources[0].url) {
            return sortedSources[0].url;
          }
        }
        
        // Fallback to last source (usually highest quality)
        const lastSource = videoData.sources[videoData.sources.length - 1];
        if (lastSource && lastSource.url) {
          return lastSource.url;
        }
      }
      
      // Fallback to direct url property
      if (videoData.url) {
        return videoData.url;
      }
      
      // Try to get from video.sources[0].url if structure is different
      if (videoData.video && videoData.video.sources && videoData.video.sources.length > 0) {
        return videoData.video.sources[0].url;
      }
      
      return null;
    } catch (e) {
      // If not JSON, treat as direct URL
      console.warn('Video lazy load: Failed to parse video data, using as direct URL', e);
      return lazySrc;
    }
  }

  /**
   * Get video attributes from the video element
   * Reads standard HTML video attributes
   */
  function getVideoAttributes(videoElement) {
    return {
      autoplay: videoElement.hasAttribute('autoplay'),
      muted: videoElement.hasAttribute('muted'),
      loop: videoElement.hasAttribute('loop'),
      playsinline: videoElement.hasAttribute('playsinline'),
      controls: videoElement.hasAttribute('controls'),
      preload: videoElement.getAttribute('preload') || 'none'
    };
  }

  /**
   * Attempt to play video with proper promise handling for Mobile Safari
   */
  function attemptAutoplay(videoElement) {
    // Ensure video is muted (required for autoplay on mobile)
    videoElement.muted = true;
    
    const playPromise = videoElement.play();
    
    if (playPromise !== undefined) {
      playPromise.catch(function(error) {
        // Autoplay was prevented - this is expected on some mobile browsers
        // The video will remain paused until user interacts
        console.warn('Video lazy load: Autoplay prevented', error);
        
        // Try once more after a short delay (sometimes helps on iOS)
        setTimeout(function() {
          videoElement.play().catch(function() {
            // Silent fail on second attempt
          });
        }, 100);
      });
    }
  }

  /**
   * Load video source into video element
   */
  function loadVideo(videoElement) {
    // Skip if already loaded
    if (state.loadedVideos.has(videoElement)) {
      return;
    }

    const videoSrc = getVideoSource(videoElement);
    if (!videoSrc) {
      console.warn('Video lazy load: No source found for element', videoElement);
      return;
    }

    // Mark as loading
    state.loadedVideos.add(videoElement);

    // Get attributes
    const attrs = getVideoAttributes(videoElement);

    // Apply attributes BEFORE setting source (important for Mobile Safari)
    // Muted must be set first for autoplay to work
    if (attrs.muted || attrs.autoplay) {
      videoElement.muted = true;
      videoElement.setAttribute('muted', '');
    }
    
    // Playsinline is required for autoplay on iOS
    if (attrs.playsinline || attrs.autoplay) {
      videoElement.setAttribute('playsinline', '');
      videoElement.setAttribute('webkit-playsinline', ''); // For older iOS
      videoElement.playsinline = true;
    }
    
    if (attrs.loop) {
      videoElement.setAttribute('loop', '');
      videoElement.loop = true;
    }
    if (attrs.controls) {
      videoElement.setAttribute('controls', '');
      videoElement.controls = true;
    }
    if (attrs.autoplay) {
      videoElement.setAttribute('autoplay', '');
      videoElement.autoplay = true;
    }
    
    videoElement.preload = attrs.autoplay ? 'auto' : attrs.preload;

    // Remove lazy loading attribute
    videoElement.removeAttribute('data-lazy-video-src');

    // Add loaded class for styling purposes
    videoElement.classList.add('video-lazy-loaded');

    // Set video source
    videoElement.src = videoSrc;
    videoElement.setAttribute('src', videoSrc);

    // Load the video
    videoElement.load();

    // For autoplay, we need to explicitly call play() for Mobile Safari
    if (attrs.autoplay) {
      // Listen for canplay event to ensure video is ready
      var playHandler = function() {
        videoElement.removeEventListener('canplay', playHandler);
        attemptAutoplay(videoElement);
      };
      
      videoElement.addEventListener('canplay', playHandler);
      
      // Also try immediately in case video is already ready (cached)
      if (videoElement.readyState >= 3) {
        attemptAutoplay(videoElement);
      }
    }

    // Dispatch custom event
    const event = new CustomEvent('videoLazyLoaded', {
      detail: { element: videoElement, src: videoSrc }
    });
    videoElement.dispatchEvent(event);
  }

  /**
   * Check if element is within threshold distance from viewport
   */
  function isWithinThreshold(element) {
    const rect = element.getBoundingClientRect();
    const windowHeight = window.innerHeight || document.documentElement.clientHeight;
    const windowWidth = window.innerWidth || document.documentElement.clientWidth;

    // Calculate distance from viewport edges
    const distanceTop = rect.top >= 0 ? 0 : Math.abs(rect.top);
    const distanceBottom = rect.bottom <= windowHeight ? 0 : rect.bottom - windowHeight;
    const distanceLeft = rect.left >= 0 ? 0 : Math.abs(rect.left);
    const distanceRight = rect.right <= windowWidth ? 0 : rect.right - windowWidth;

    // Get minimum distance (closest edge)
    const minDistance = Math.min(
      distanceTop,
      distanceBottom,
      distanceLeft,
      distanceRight
    );

    return minDistance <= CONFIG.threshold;
  }

  /**
   * Intersection Observer callback
   */
  function handleIntersection(entries, observer) {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        const videoElement = entry.target;
        loadVideo(videoElement);
        // Unobserve after loading to improve performance
        observer.unobserve(videoElement);
      }
    });
  }

  /**
   * Find all video elements that need lazy loading
   */
  function findLazyVideos() {
    return Array.from(document.querySelectorAll('video[data-lazy-video-src]'));
  }

  /**
   * Initialize Intersection Observer
   */
  function initObserver() {
    if (!window.IntersectionObserver) {
      // Fallback for browsers without IntersectionObserver
      return initFallback();
    }

    const options = {
      root: null,
      rootMargin: CONFIG.rootMargin,
      threshold: 0
    };

    state.observer = new IntersectionObserver(handleIntersection, options);
    return true;
  }

  /**
   * Fallback for browsers without IntersectionObserver
   */
  function initFallback() {
    let ticking = false;

    function checkVideos() {
      const videos = findLazyVideos();
      videos.forEach(video => {
        if (!state.loadedVideos.has(video) && isWithinThreshold(video)) {
          loadVideo(video);
        }
      });
      ticking = false;
    }

    function onScroll() {
      if (!ticking) {
        window.requestAnimationFrame(checkVideos);
        ticking = true;
      }
    }

    // Initial check
    checkVideos();

    // Listen to scroll events
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });

    return false;
  }

  /**
   * Initialize lazy loading
   */
  function init() {
    if (state.initialized) return;
    state.initialized = true;

    const videos = findLazyVideos();
    
    if (videos.length === 0) {
      return;
    }

    // Try to use IntersectionObserver
    const useObserver = initObserver();

    if (useObserver && state.observer) {
      // Observe all videos
      videos.forEach(video => {
        state.observer.observe(video);
      });
    }

    // Also check immediately for videos already in viewport
    videos.forEach(video => {
      if (isWithinThreshold(video)) {
        loadVideo(video);
        if (state.observer) {
          state.observer.unobserve(video);
        }
      }
    });
  }

  /**
   * Re-initialize for dynamically added content
   */
  function reinit() {
    const videos = findLazyVideos();
    const newVideos = videos.filter(v => !state.loadedVideos.has(v));

    if (newVideos.length === 0) return;

    if (state.observer) {
      newVideos.forEach(video => {
        if (isWithinThreshold(video)) {
          loadVideo(video);
        } else {
          state.observer.observe(video);
        }
      });
    } else {
      // Fallback mode - check immediately
      newVideos.forEach(video => {
        if (isWithinThreshold(video)) {
          loadVideo(video);
        }
      });
    }
  }

  // Initialize on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-initialize when new content is added (for dynamic sections)
  if (window.MutationObserver) {
    const mutationObserver = new MutationObserver(function(mutations) {
      let shouldReinit = false;
      mutations.forEach(mutation => {
        if (mutation.addedNodes.length > 0) {
          mutation.addedNodes.forEach(node => {
            if (node.nodeType === 1) { // Element node
              if (node.tagName === 'VIDEO' && node.hasAttribute('data-lazy-video-src')) {
                shouldReinit = true;
              } else if (node.querySelectorAll && node.querySelectorAll('video[data-lazy-video-src]').length > 0) {
                shouldReinit = true;
              }
            }
          });
        }
      });
      if (shouldReinit) {
        setTimeout(reinit, 100); // Small delay to ensure DOM is ready
      }
    });

    mutationObserver.observe(document.body, {
      childList: true,
      subtree: true
    });
  }

  // Expose API for manual control
  window.VideoLazyLoad = {
    init: init,
    reinit: reinit,
    loadVideo: loadVideo,
    findLazyVideos: findLazyVideos
  };
})();

